import React, { useState } from 'react';
import { 
  Users, 
  Mic, Video, Zap, 
  X, Watch, UserPlus, ShieldAlert,
  Compass, Lock, Sparkles, Heart, Power, User
} from 'lucide-react';
import { MAX_AGILITY_CAP, MAX_CHARISMA_CAP } from '../../utils/xpProgression';
import TileDetailModal from './TileDetailModal';

export const TILES_METADATA = {
  // CATÉGORIE A : DÉCLENCHEMENT D'ALERTE
  smartwatch: {
    id: 'smartwatch',
    category: 'alert',
    title: 'Montre Connectée (Smartwatch)',
    iconName: 'Watch',
    costXP: 60,
    costPVReaction: '⚡ Instantané (Gratuit)',
    shortDesc: 'Bouton SOS d’urgence direct au poignet',
    usage: "Bouton SOS d'urgence déclenché directement depuis l'écran ou le bouton physique de votre montre connectée au poignet.",
    forces: [
      "Parfait dans TOUTES les circonstances (téléphone dans le sac, dans la poche ou en main).",
      "Déclenchement ultra-discret au poignet, indétectable par l'agresseur.",
      "Inarrêtable et instantané (aucune prise pour l'agresseur)."
    ],
    faiblesses: [
      "Nécessite de posséder une montre connectée appairée (coût d'investissement de 60 XP)."
    ]
  },
  shortcutHardware: {
    id: 'shortcutHardware',
    category: 'alert',
    title: 'Touche Raccourcis (Volume/Power)',
    iconName: 'Power',
    costXP: 60,
    costPVReaction: '8 Agilité / 6 Sang-froid (PV)',
    shortDesc: 'Combinaison Volume ou 5 appuis rapides sur Power',
    usage: "Combinaison discrète des boutons physiques de volume, ou 5 impulsions consécutives et rapides sur le bouton latéral Power, pour déclencher l'alerte silencieuse.",
    forces: [
      "Parfait et fonctionnel dans toutes les circonstances (dans la poche, au fond du sac, en main).",
      "Très discret, peut être déclenché à l'aveugle sans regarder l'écran.",
      "Fonctionne même avec l'écran du smartphone verrouillé."
    ],
    faiblesses: [
      "Risque d'accrochage ou d'arrachage si l'agresseur tente de saisir le mobile.",
      "Nécessite d'effectuer le geste de manière rythmée sous l'effet du stress."
    ]
  },
  // CATÉGORIE B : AIDE À L’ENQUÊTE ET À L’INTERVENTION
  guardians: {
    id: 'guardians',
    category: 'investigation',
    title: 'Alerter la Communauté MonAlerte',
    iconName: 'Users',
    costXP: 60,
    costPVReaction: 'Inclus dans le SOS',
    shortDesc: 'Mobilise les Protecteurs proches (270m)',
    usage: "Mobilise les Protecteurs citoyens MonAlerte situés dans un rayon de 270 mètres autour de la victime lors du déclenchement du SOS.",
    forces: [
      "Intervention physique rapide de voisins et citoyens bénévoles pour faire fuir l'agresseur.",
      "Témoignages oculaires multiples permettant l'identification et l'arrestation du suspect.",
      "Dissuade l'agresseur et interrompt l'agression avant son terme."
    ],
    faiblesses: [
      "Dépend de la présence et de la disponibilité de Protecteurs actifs dans le périmètre.",
      "Ne dispense pas de l'alerte des forces de l'ordre officielles."
    ]
  },
  contacts: {
    id: 'contacts',
    category: 'investigation',
    title: 'Contacts de Confiance 2.0',
    iconName: 'Users',
    costXP: 10,
    costPVReaction: 'Inclus dans le SOS',
    shortDesc: 'Alerte SMS & live géolocalisé aux proches',
    usage: "Envoi immédiat d'un SMS prioritaire avec lien de géolocalisation live sécurisé à vos contacts de confiance lors de tout déclenchement SOS.",
    forces: [
      "Vos proches reçoivent instantanément votre alerte et votre position exacte en temps réel.",
      "Prise en charge personnalisée et possibilité de rassurer ou d'appeler les secours pour vous.",
      "Incrémentation permanente (+1 contact pour 10 XP)."
    ],
    faiblesses: [
      "Les proches ne sont pas toujours disponibles ou dans le même secteur géographique.",
      "On ne peut pas revenir en arrière après déblocage d'un contact."
    ]
  },
  audioLive: {
    id: 'audioLive',
    category: 'investigation',
    title: 'Capture Audio',
    iconName: 'Mic',
    costXP: 60,
    costPVReaction: 'Automatique au SOS',
    shortDesc: 'Micro d’urgence live 60s vers coffre Cloud',
    usage: "Enregistrement vocal et sonore ambiant d'urgence de 60 secondes, transmis et scellé au coffre-fort numérique.",
    forces: [
      "Preuve inaltérable scellée avec horodatage certifié pour le dépôt de plainte pénale, le Parquet et l'assurance.",
      "Capture les paroles, menaces verbales, bruits ambiants et aveux de l'agresseur."
    ],
    faiblesses: [
      "Si le téléphone est dans le sac -> on n'entendra rien du tout (son inaudible).",
      "Si le téléphone est dans la poche -> son très étouffé et dégradé.",
      "Ne permet PAS d'identifier le visage de l'agresseur sans vidéo ou témoins."
    ]
  },
  cameraCloud: {
    id: 'cameraCloud',
    category: 'investigation',
    title: 'Capture Vidéo',
    iconName: 'Video',
    costXP: 60,
    costPVReaction: 'Automatique au SOS',
    shortDesc: 'Flash Cloud & Identification faciale',
    usage: "Flux vidéo direct transmis en temps réel et scellé dans le Cloud sans trace sur l'appareil.",
    forces: [
      "Si le téléphone est en dehors de la poche (en main) -> capture nette du visage et tenue du suspect (identification et arrestation à 100%).",
      "Preuve juridique absolue pour la plainte pénale, le tribunal et les compagnies d'assurance."
    ],
    faiblesses: [
      "Si le téléphone reste dans la poche ou dans le sac -> objectif masqué (écran noir, inexploitable).",
      "Nécessite que la Capture Audio soit également activée."
    ]
  }
};

export const ATTACKER_RPG_POOL = [
  {
    id: 'thief_fast',
    alias: "L'Arracheur Furtif",
    avatar: "🦹‍♂️",
    category: "Voleur à la Tire",
    maxHp: 65,
    charismaHp: 65,
    speedStat: "Sprint Véloce (2.2x)",
    weakness: "cameraCloud",
    weaknessLabel: "🎥 Capture Vidéo Cloud (-20 PV)",
    bio: "Attaque rapide en effleurement. Tente d'arracher l'écran des mains si vous regardez l'application.",
  },
  {
    id: 'brute_force',
    alias: "Le Colosse Intimidant",
    avatar: "🦹",
    category: "Agression Frontale",
    maxHp: 90,
    charismaHp: 90,
    speedStat: "Puissant (Sprint 2.0x)",
    weakness: "guardians",
    weaknessLabel: "🛡️ Protecteurs Athlétiques (Dégâts x1.5)",
    bio: "Attaque imposante et menaçante visant à sidérer la victime sur le trottoir.",
  },
  {
    id: 'stalker_night',
    alias: "Le Rôdeur Nocturne",
    avatar: "👤🦹",
    category: "Agression en Ruelle",
    maxHp: 75,
    charismaHp: 75,
    speedStat: "Furtif (Sprint 2.1x)",
    weakness: "audioLive",
    weaknessLabel: "🎙️ Capture Audio (-15 PV par voix enregistrée)",
    bio: "Plaque la victime contre un mur dans l'obscurité pour lui dérober ses effets personnels.",
  },
  {
    id: 'pickpocket_pro',
    alias: "L'Arnaqueur Récidiviste",
    avatar: "🎭🦹",
    category: "Vol à la Tire Pro",
    maxHp: 60,
    charismaHp: 60,
    speedStat: "Très Rapide (Sprint 2.3x)",
    weakness: "guardians",
    weaknessLabel: "🛡️ Protecteurs au Contact",
    bio: "Subtilise avec dextérité. Panique très vite dès que des citoyens s'approchent.",
  }
];

// Fonction utilitaire : 25% de chance d'avoir le contre sur l'Application SOS, 75% aucun contre
export const getRandomAttacker = () => {
  const base = ATTACKER_RPG_POOL[Math.floor(Math.random() * ATTACKER_RPG_POOL.length)];
  const hasAppCounter = Math.random() < 0.25; // 25% de contre sur l'application uniquement
  return {
    ...base,
    immunity: hasAppCounter ? 'app_sos' : null,
    immunityLabel: hasAppCounter ? "📱 Bouton SOS de l'App (Tentative d'arrachage du mobile)" : "Aucune (Vulnérable à tous les SOS)",
  };
};

const GameSetupModal = ({
  isOpen,
  onClose,
  selectedRole,
  setSelectedRole,
  parentTiles,
  setParentTiles,
  childProfile,
  setChildProfile,
  userRPG,
  setUserRPG,
  onStartGame,
  userXP = 40,
  userPV = 55,
  unlockedFeatures = [],
  onUnlockFeature,
  trustedContactsCount = 0,
  onAddTrustedContact,
}) => {
  const [childNameInput, setChildNameInput] = useState(childProfile?.name?.split(' ')[0] || 'Lucas');
  const [childAgeInput, setChildAgeInput] = useState('10');
  
  // Attributs puisés dans le solde de PV (Initialement : 30 PV Agilité, 25 PV Charisme)
  const [agility, setAgility] = useState(() => {
    return userRPG?.agility !== undefined ? userRPG.agility : 30;
  });
  const [composure, setComposure] = useState(() => {
    return userRPG?.composure !== undefined ? userRPG.composure : 25;
  });
  const [phoneMode, setPhoneMode] = useState(userRPG?.phoneMode || 'in_bag');
  const [unlockFeedback, setUnlockFeedback] = useState(null); // { message, success }
  const [selectedTileDetail, setSelectedTileDetail] = useState(null);

  React.useEffect(() => {
    if (userRPG?.phoneMode) {
      setPhoneMode(userRPG.phoneMode);
    }
    if (userRPG?.agility) {
      setAgility(userRPG.agility);
    }
    if (userRPG?.composure) {
      setComposure(userRPG.composure);
    }
  }, [isOpen, userRPG]);

  if (!isOpen) return null;

  const handleUnlockClick = (featureId, costXP = 0, costPV = 0, label = '') => {
    if (userXP < costXP) {
      setUnlockFeedback({
        success: false,
        message: `❌ XP insuffisante pour "${label}" (Requis : ${costXP} XP | Solde : ${userXP} XP). Réussissez des détections pour en gagner !`,
      });
      setTimeout(() => setUnlockFeedback(null), 4000);
      return;
    }

    if (onUnlockFeature) {
      const success = onUnlockFeature(featureId, costXP, 0);
      if (success) {
        setUnlockFeedback({
          success: true,
          message: `🎉 "${label}" débloqué avec succès (-${costXP} XP) !`,
        });
        setTimeout(() => setUnlockFeedback(null), 3000);
      }
    }
  };

  const handleConfirmStart = () => {
    if (selectedRole === 'parent' && setChildProfile) {
      setChildProfile({
        id: 'child-1',
        name: `${childNameInput.trim() || 'Lucas'} (${childAgeInput || '10'} ans)`,
        theme: 'Océan Bleuté',
      });
    }

    // Tirer secrètement l'agresseur sans le révéler à l'avance
    const secretAttacker = ATTACKER_RPG_POOL[Math.floor(Math.random() * ATTACKER_RPG_POOL.length)];

    const updatedUserRPG = {
      name: 'Sarah',
      agility,
      composure,
      phoneMode,
      phoneInHandLevel: phoneMode === 'in_hand' ? 90 : phoneMode === 'in_pocket' ? 50 : 15,
      trustedContactsCount,
      isContacts20: true,
    };

    if (setUserRPG) {
      setUserRPG(updatedUserRPG);
    }

    onStartGame({
      role: selectedRole,
      childName: `${childNameInput.trim() || 'Lucas'} (${childAgeInput || '10'} ans)`,
      userRPG: updatedUserRPG,
      attackerRPG: secretAttacker,
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(3, 7, 18, 0.65)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      animation: 'fadeIn 0.2s ease',
    }}>
      <div style={{
        background: 'linear-gradient(145deg, #0b1f3a 0%, #060e1b 100%)',
        border: '1.5px solid rgba(242, 184, 7, 0.45)',
        borderRadius: '22px',
        maxWidth: '680px',
        width: '100%',
        maxHeight: '86vh',
        overflowY: 'auto',
        padding: '20px 22px',
        boxShadow: '0 25px 80px rgba(0, 0, 0, 0.95)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {/* En-tête Épuré de la Popup Profil */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(242, 184, 7, 0.15)', border: '1px solid var(--mga-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={20} color="var(--mga-gold)" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#ffffff', fontWeight: 800 }}>
                👤 Profil & Déclenchements de Détection
              </h3>
              <span style={{ fontSize: '0.74rem', color: 'var(--mga-slate-400)' }}>
                Attributs de survie (Agilité / Sang-froid) et technologies d'alerte
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              color: '#cbd5e1',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Feedback de Déblocage */}
        {unlockFeedback && (
          <div style={{
            background: unlockFeedback.success ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            border: '1px solid ' + (unlockFeedback.success ? '#10b981' : '#ef4444'),
            borderRadius: '10px',
            padding: '8px 14px',
            fontSize: '0.80rem',
            color: unlockFeedback.success ? '#86efac' : '#fca5a5',
            fontWeight: 700,
            animation: 'fadeIn 0.2s ease',
          }}>
            {unlockFeedback.message}
          </div>
        )}

        {/* ÉTAPE 2 : ATTRIBUTS RPG & CONTACTS (UTILISATEUR SOLO) */}
        {selectedRole === 'user' && (
          <div style={{
            background: 'rgba(15, 39, 74, 0.6)',
            border: '1.5px solid rgba(56, 189, 248, 0.35)',
            borderRadius: '18px',
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#38bdf8', fontWeight: 800, textTransform: 'uppercase' }}>
                  <Compass size={16} /> ⚡ 1. Vos Attributs de Survie (Agilité & Sang-froid)
                </label>
                <span style={{ fontSize: '0.74rem', color: (agility + composure) > userPV ? '#f87171' : '#86efac', fontWeight: 700 }}>
                  💖 Allocation : {agility + composure} / {userPV} PV alloués ({Math.max(0, userPV - agility - composure)} PV restants)
                </span>
              </div>
              <div style={{ display: 'flex', gap: '10px', fontSize: '0.74rem' }}>
                <span style={{ color: '#38bdf8', fontWeight: 700 }}>⚡ Agi: {agility}</span>
                <span style={{ color: '#4ade80', fontWeight: 700 }}>🛡️ Cha: {composure}</span>
                <span style={{ color: 'var(--mga-gold)', fontWeight: 700 }}>📲 Contacts: {trustedContactsCount}</span>
              </div>
            </div>

            {/* Tableau de bord de répartition des PV */}
            <div style={{
              background: 'rgba(5, 11, 20, 0.75)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              borderRadius: '12px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Heart size={18} color="#ef4444" fill="#ef4444" />
                <span style={{ fontSize: '0.82rem', color: '#ffffff', fontWeight: 800 }}>
                  Réserve de Vitalité : <strong style={{ color: '#fca5a5' }}>{userPV} PV disponibles</strong>
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', fontSize: '0.76rem' }}>
                <span style={{ color: '#38bdf8', fontWeight: 700 }}>⚡ Agilité : {agility}/{MAX_AGILITY_CAP} PV</span>
                <span style={{ color: '#4ade80', fontWeight: 700 }}>🛡️ Charisme : {composure}/{MAX_CHARISMA_CAP} PV</span>
                <span style={{ color: (userPV - agility - composure) < 0 ? '#f87171' : '#fde047', fontWeight: 800 }}>
                  🔋 Libre : {Math.max(0, userPV - agility - composure)} PV
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '14px' }}>
              {/* Critère 1 : Agilité (Jauge jusqu'à MAX_AGILITY_CAP) */}
              <div style={{ background: '#050b14', padding: '14px', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
                    <Zap size={15} color="#38bdf8" /> 1. Agilité (Vitesse) :
                  </span>
                  <strong style={{ fontSize: '0.95rem', color: '#38bdf8' }}>{agility} / {MAX_AGILITY_CAP} PV</strong>
                </div>
                <input
                  type="range"
                  min="1"
                  max={MAX_AGILITY_CAP}
                  value={agility}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    const maxAllowed = Math.min(MAX_AGILITY_CAP, Math.max(1, userPV - composure));
                    setAgility(Math.min(val, maxAllowed));
                  }}
                  style={{ width: '100%', accentColor: '#38bdf8', cursor: 'pointer', marginBottom: '6px' }}
                />
                <span style={{ fontSize: '0.7rem', color: 'var(--mga-slate-400)', display: 'block', lineHeight: 1.3 }}>
                  {MAX_AGILITY_CAP} PV nécessaires pour activer toutes les détections cumulées.
                </span>
              </div>

              {/* Critère 2 : Charisme & Sang-froid (Jauge jusqu'à MAX_CHARISMA_CAP) */}
              <div style={{ background: '#050b14', padding: '14px', borderRadius: '14px', border: '1px solid rgba(74, 222, 128, 0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
                    <ShieldAlert size={15} color="#4ade80" /> 2. Charisme & Sang-froid :
                  </span>
                  <strong style={{ fontSize: '0.95rem', color: '#4ade80' }}>{composure} / {MAX_CHARISMA_CAP} PV</strong>
                </div>
                <input
                  type="range"
                  min="1"
                  max={MAX_CHARISMA_CAP}
                  value={composure}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    const maxAllowed = Math.min(MAX_CHARISMA_CAP, Math.max(1, userPV - agility));
                    setComposure(Math.min(val, maxAllowed));
                  }}
                  style={{ width: '100%', accentColor: '#4ade80', cursor: 'pointer', marginBottom: '6px' }}
                />
                <span style={{ fontSize: '0.7rem', color: 'var(--mga-slate-400)', display: 'block', lineHeight: 1.3 }}>
                  {MAX_CHARISMA_CAP} PV nécessaires pour résister à toutes les intimidations.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ÉTAPE 2 (BIS) : ENFANT (SI MODE PARENT) */}
        {selectedRole === 'parent' && (
          <div style={{
            background: 'rgba(15, 39, 74, 0.6)',
            border: '1.5px solid rgba(242, 184, 7, 0.3)',
            borderRadius: '18px',
            padding: '18px 20px',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: 'var(--mga-gold)', fontWeight: 800, marginBottom: '12px', textTransform: 'uppercase' }}>
              <UserPlus size={16} /> 2. Informations de votre Enfant
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '14px' }}>
              <div>
                <span style={{ fontSize: '0.78rem', color: '#cbd5e1', display: 'block', marginBottom: '6px' }}>
                  Prénom de l'enfant :
                </span>
                <input
                  type="text"
                  value={childNameInput}
                  onChange={(e) => setChildNameInput(e.target.value)}
                  placeholder="Ex: Lucas, Emma, Chloé..."
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid rgba(242, 184, 7, 0.4)',
                    background: '#050b14',
                    color: '#ffffff',
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <span style={{ fontSize: '0.78rem', color: '#cbd5e1', display: 'block', marginBottom: '6px' }}>
                  Âge de l'enfant :
                </span>
                <select
                  value={childAgeInput}
                  onChange={(e) => setChildAgeInput(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid rgba(242, 184, 7, 0.4)',
                    background: '#050b14',
                    color: '#ffffff',
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    outline: 'none',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(age => (
                    <option key={age} value={age} style={{ background: '#0b1f3a', color: '#fff' }}>
                      {age} ans
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ÉTAPE 3 : TUILES DE PROTECTION & DÉBLOCAGES */}
        {selectedRole !== 'attacker' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Barre de Solde XP & Explication */}
            <div style={{
              background: 'rgba(5, 11, 20, 0.75)',
              border: '1px solid rgba(242, 184, 7, 0.35)',
              borderRadius: '12px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="var(--mga-gold)" />
                <span style={{ fontSize: '0.82rem', color: '#ffffff', fontWeight: 800 }}>
                  Solde de Progression : <strong style={{ color: 'var(--mga-gold)' }}>{userXP} XP disponibles</strong>
                </span>
              </div>
              <span style={{ fontSize: '0.74rem', color: '#cbd5e1' }}>
                💡 Cliquez sur n'importe quelle tuile pour voir ses forces, faiblesses, coût en PV et la débloquer
              </span>
            </div>

            {/* SOUS-SECTION A : DÉCLENCHEMENT DE L'ALERTE */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontSize: '0.86rem', color: '#f87171', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🚨 2. Déclenchement de l'Alerte (Actions de Réaction)
                </label>
                <span style={{ fontSize: '0.72rem', color: 'var(--mga-slate-400)' }}>
                  Méthodes pour lancer le SOS selon l'emplacement
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                {['smartwatch', 'shortcutHardware'].map(key => {
                  const meta = TILES_METADATA[key];
                  const isUnlocked = unlockedFeatures.includes(key);
                  const IconComp = key === 'smartwatch' ? Watch : Power;

                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedTileDetail(meta)}
                      style={{
                        background: isUnlocked 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(5, 150, 105, 0.28) 100%)' 
                          : 'rgba(239, 68, 68, 0.06)',
                        border: isUnlocked ? '2px solid #10b981' : '1.5px solid rgba(239, 68, 68, 0.35)',
                        borderRadius: '14px',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: isUnlocked ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                        border: isUnlocked ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <IconComp size={19} color={isUnlocked ? '#4ade80' : '#f87171'} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.85rem', color: '#ffffff', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>{meta.title.split(' (')[0]}</span>
                        </div>
                        <span style={{ fontSize: '0.70rem', color: isUnlocked ? '#a7f3d0' : '#fca5a5', display: 'block', lineHeight: 1.3 }}>
                          {isUnlocked ? meta.shortDesc : `Cliquez pour infos (${meta.costXP} XP)`}
                        </span>
                      </div>
                      <div>
                        {isUnlocked ? (
                          <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#4ade80', background: 'rgba(74, 222, 128, 0.2)', border: '1px solid #10b981', padding: '3px 8px', borderRadius: '6px' }}>🟢 ACTIF</span>
                        ) : (
                          <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#f87171', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.35)', padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><Lock size={11} /> {meta.costXP} XP</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* SOUS-SECTION B : AIDE À L'ENQUÊTE ET À L'INTERVENTION */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontSize: '0.86rem', color: '#38bdf8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🔒 3. Aide à l'Enquête et à l'Intervention
                </label>
                <span style={{ fontSize: '0.72rem', color: 'var(--mga-slate-400)' }}>
                  Mobilisation citoyenne, proches et scellés judiciaires
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                {['guardians', 'contacts', 'audioLive', 'cameraCloud'].map(key => {
                  const meta = TILES_METADATA[key];
                  const isUnlocked = key === 'contacts' ? trustedContactsCount > 0 : unlockedFeatures.includes(key);
                  const IconComp = (key === 'guardians' || key === 'contacts') ? Users : key === 'audioLive' ? Mic : Video;

                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedTileDetail(meta)}
                      style={{
                        background: isUnlocked 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(5, 150, 105, 0.28) 100%)' 
                          : 'rgba(239, 68, 68, 0.06)',
                        border: isUnlocked ? '2px solid #10b981' : '1.5px solid rgba(239, 68, 68, 0.35)',
                        borderRadius: '14px',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        background: isUnlocked ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                        border: isUnlocked ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <IconComp size={19} color={isUnlocked ? '#4ade80' : '#f87171'} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.85rem', color: '#ffffff', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>{meta.title.split(' (')[0]}</span>
                        </div>
                        <span style={{ fontSize: '0.70rem', color: isUnlocked ? '#a7f3d0' : '#fca5a5', display: 'block', lineHeight: 1.3 }}>
                          {key === 'contacts' 
                            ? (trustedContactsCount > 0 ? `${trustedContactsCount} contact(s) de confiance configuré(s)` : `Cliquez pour ajouter (10 XP / contact)`)
                            : (isUnlocked ? meta.shortDesc : `Cliquez pour infos (${meta.costXP} XP)`)}
                        </span>
                      </div>
                      <div>
                        {key === 'contacts' ? (
                          trustedContactsCount > 0 ? (
                            <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#4ade80', background: 'rgba(74, 222, 128, 0.2)', border: '1px solid #10b981', padding: '3px 8px', borderRadius: '6px' }}>🟢 {trustedContactsCount} ACTIF(S)</span>
                          ) : (
                            <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#f87171', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.35)', padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><Lock size={11} /> 10 XP/c</span>
                          )
                        ) : isUnlocked ? (
                          <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#4ade80', background: 'rgba(74, 222, 128, 0.2)', border: '1px solid #10b981', padding: '3px 8px', borderRadius: '6px' }}>🟢 ACTIF</span>
                        ) : (
                          <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#f87171', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.35)', padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><Lock size={11} /> {meta.costXP} XP</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* BOUTON ENREGISTRER */}
        <button
          onClick={() => {
            handleConfirmStart();
            onClose();
          }}
          style={{
            background: 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)',
            color: '#000000',
            border: 'none',
            padding: '14px',
            borderRadius: '14px',
            fontWeight: 900,
            fontSize: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 6px 20px rgba(242, 184, 7, 0.4)',
            marginTop: '4px',
          }}
        >
          <span>💾 ENREGISTRER LE PROFIL & FERMER</span>
        </button>
      </div>

      {/* POPUP DÉTAILLÉE DES TUILES DE PROTECTION & ENQUÊTE */}
      <TileDetailModal
        isOpen={Boolean(selectedTileDetail)}
        onClose={() => setSelectedTileDetail(null)}
        tileInfo={selectedTileDetail}
        isUnlocked={
          selectedTileDetail?.id === 'contacts' 
            ? trustedContactsCount > 0 
            : (selectedTileDetail ? unlockedFeatures.includes(selectedTileDetail.id) : false)
        }
        trustedContactsCount={trustedContactsCount}
        userXP={userXP}
        onAddContact={onAddTrustedContact}
        onUnlock={(featureId, costXP, label) => {
          handleUnlockClick(featureId, costXP, 0, label);
        }}
      />
    </div>
  );
};

export default GameSetupModal;
